import { inject, Injectable } from '@angular/core';
import { map, type Observable, of } from 'rxjs';

import { type EngineStateBase } from '@blaze-runtime/engine-data-access';
import {
  type CanvasElement,
  type CanvasElementFrom,
  type CanvasElementId,
  type ColumnId,
} from '@blaze-runtime/engine-domain';
import { type ConnectionId } from '@blaze-shared/api-contracts-domain';
import {
  type DataBindingSourceInfo,
  type DataBindingSourceInfoColumn,
  getActiveBinding,
  type PregenerationType,
  type WizardActionType,
} from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { getColumnKey } from '@blaze-shared/schema-domain';
import { deepClone, isEqual } from '@blaze-shared/shared-util';
import { type StyleSheet } from '@blaze-shared/theming-domain';

import { resolveWizardDatasetId } from '../../../elements/wizard/wizard.utils';
import { ElementDefinitionsService } from '../../element-definitions.service';

import { FormGenerationSourceService } from './form-generation-source.service';
import { FormGenerationService } from './form-generation.service';
import { applyQuestionFieldsMutably } from './wizard-question-fields';
import { applyQuestionFormsMutably } from './wizard-question-forms';
import { applyQuestionTextsMutably } from './wizard-question-texts';
import {
  applyQuestionnaireSlotsMutably,
  type QuestionnaireSlotsResult,
} from './wizard-questionnaire-slots';
import { collectOrphanedStepActionConnections } from './wizard-questionnaire-step-actions';

/** Which pregeneration a questionnaire's question forms use, given the wizard's own action. */
const WIZARD_ACTION_PREGENERATION: Record<WizardActionType, PregenerationType> = {
  add: 'add',
  edit: 'edit',
  link: 'view',
};

/** What a questionnaire regeneration needs beyond the state it writes into. */
export interface QuestionnaireGenerationData {
  wizardId: CanvasElementId;
  /**
   * The one dataset every question form is generated from, with its columns already loaded —
   * a questionnaire asks about the columns of a single dataset.
   *
   * Absent while the wizard has no data source to resolve. The steps still own the slots then,
   * so the regeneration runs; there is simply nothing to put in them.
   */
  dataSource: DataBindingSourceInfo | undefined;
  pregenerationType: PregenerationType;
  /** {@link FormGenerationData.accountsTableName} */
  accountsTableName: string | undefined;
}

/** One regeneration's effect on the document: what the commit action carries, plus what it minted. */
export interface QuestionnaireRegenerationResult {
  elementIdsToRemove: CanvasElementId[];
  elementsToUpsert: CanvasElement[];
  /** {@link collectOrphanedStepActionConnections} */
  connectionIdsToRemove: ConnectionId[];
  /**
   * The slots this pass minted, for steps that had none. Deliberately *not* part of the commit:
   * it says nothing about the document, only that these steps are new to the canvas, which is
   * what lets the editor follow the builder onto a step they have just added.
   */
  addedSlotIds: ColumnId[];
  /**
   * {@link QuestionnaireSlotsResult.resultingSlotIds} — out of the commit for the same reason:
   * it is what the editor measures its own slot selection against, the way the commit's
   * `dropStaleSlotSelectionMutably` does.
   */
  resultingSlotIds: ColumnId[];
}

/**
 * Regenerates a questionnaire wizard: its slots, the one-field form behind each question, the
 * field inside each of those forms, and the Text elements that carry the question itself.
 *
 * **Declarative.** The wizard's props are the desired state and everything else is derived
 * from them, so a regeneration is idempotent — running it twice produces an empty second
 * diff. Nothing here learns *why* the props changed; a data source swap and a deleted
 * question reach it as the same request.
 *
 * The whole pass is one synchronous call over one draft, so the editor commits it as a single
 * action and the builder undoes it in one step. The asynchronous half — resolving the dataset
 * and preloading the schemas the generation reads — happens once up front, in
 * {@link prepareGenerationDataAndPreloadSchemas}.
 */
@Injectable({ providedIn: 'root' })
export class WizardQuestionnaireGenerationService {
  private readonly elementDefinitions = inject(ElementDefinitionsService);
  private readonly formGeneration = inject(FormGenerationService);
  private readonly formGenerationSource = inject(FormGenerationSourceService);
  private readonly schemaService = inject(SchemaService);

  /**
   * Resolves the dataset the questionnaire is built on and preloads every schema the
   * generation will read, so the generation itself can run synchronously. One call covers the
   * whole wizard: all its question forms share the one data source.
   *
   * A wizard with no resolvable data source still regenerates — its steps own its slots either
   * way, and a mode switch reaches this with the steps already cleared — so the data comes back
   * without a source rather than not at all.
   */
  prepareGenerationDataAndPreloadSchemas(
    wizard: CanvasElementFrom<'wizard'>,
  ): Observable<QuestionnaireGenerationData> {
    // two reads of one binding: resolveWizardDatasetId turns its context workflow id into the
    // DATASET id everything downstream is keyed by - shared with the Add Question pane, so the
    // columns offered are the columns generated from - while the binding itself carries the
    // name and columns the dataset-shaped source below spreads.
    const wizardSource = getActiveBinding(wizard.props.data, 'value')?.dataSource;
    const datasetId = resolveWizardDatasetId(this.schemaService, wizard.props);
    const pregenerationType = WIZARD_ACTION_PREGENERATION[wizard.props.content.action ?? 'add'];

    if (!wizardSource || !datasetId) {
      return of({
        wizardId: wizard.id,
        dataSource: undefined,
        pregenerationType,
        accountsTableName: undefined,
      });
    }

    const datasetSource: DataBindingSourceInfo = {
      ...wizardSource,
      sourceType: 'dataset',
      id: datasetId,
    };

    return this.formGenerationSource
      .prepareFormGenerationDataAndPreloadSchemas(undefined, { pregenerationType }, datasetSource)
      .pipe(
        map(({ inputDataSource, accountsTableName }) => {
          if (!inputDataSource) {
            throw new Error('questionnaire data source missing');
          }
          return {
            wizardId: wizard.id,
            dataSource: inputDataSource,
            pregenerationType,
            accountsTableName,
          };
        }),
      );
  }

  /**
   * Runs the whole regeneration against a copy of `state` and reports what changed.
   *
   * The commit carries a diff rather than a whole entity state so that regenerations
   * overlapping with other editor writes cannot silently revert them (BL-9502). The action
   * layer is read off the regenerated state the same way: the step-action wiring the new steps
   * leave orphaned goes out with the element diff, so both slices commit as one.
   */
  regenerateNonMutably(
    state: EngineStateBase,
    styleSheets: StyleSheet[],
    data: QuestionnaireGenerationData,
  ): QuestionnaireRegenerationResult {
    const nextState = deepClone(state);
    const { addedSlotIds, resultingSlotIds } = this.regenerateMutably(nextState, styleSheets, data);

    return {
      ...diffElements(state, nextState),
      connectionIdsToRemove: collectOrphanedStepActionConnections(nextState, data.wizardId),
      addedSlotIds,
      resultingSlotIds,
    };
  }

  /**
   * The regeneration itself, written straight into `state`: slots follow the steps, forms
   * follow the slots, fields follow the forms, and each question's texts and field defaults
   * follow the form they live in.
   *
   * Everything but the slots needs the dataset the questions ask about, so without a data
   * source the pass stops after them. What the steps orphan in the action layer is not written
   * here at all — see {@link regenerateNonMutably}.
   *
   * @returns which slots matching the steps added and dropped
   */
  regenerateMutably(
    state: EngineStateBase,
    styleSheets: StyleSheet[],
    { wizardId, dataSource, pregenerationType, accountsTableName }: QuestionnaireGenerationData,
  ): QuestionnaireSlotsResult {
    const slots = applyQuestionnaireSlotsMutably(state, wizardId);
    if (!dataSource) {
      return slots;
    }

    const forms = applyQuestionFormsMutably(state, wizardId, {
      dataSource,
      styleSheets,
      formContainerDefinition: this.elementDefinitions.getDefinitionSafe('formContainer'),
    });

    for (const { formId, columnKey, isNewlyPlaced } of forms) {
      this.formGeneration.regenerateFormMutably(state, styleSheets, {
        formId,
        inputDataSource: dataSource,
        pregenerationType,
        accountsTableName,
        isNewDataSource: isNewlyPlaced,
        // why: a question's form shows one column, and that is a birth condition rather than a
        // standing rule. Deciding it here, once, is what keeps every later regeneration from
        // fighting the fields the builder has since revealed in the form's own panel.
        overrideColumnVisibility: isNewlyPlaced
          ? singleFieldVisibility(dataSource.columns, columnKey)
          : undefined,
      });
    }

    // Both passes read what the form generation just produced, so they come last: the texts
    // land above the generated field, and the field itself is only there to be found now.
    applyQuestionTextsMutably(
      state,
      wizardId,
      forms,
      this.elementDefinitions.getDefinitionSafe('textArea'),
    );
    applyQuestionFieldsMutably(state, wizardId, forms);

    return slots;
  }
}

/**
 * Visibility override generating a question's one-field form: every column of the dataset
 * forced hidden except the question's own. Forcing the question's column to `true` also wins
 * over the default exclusions (e.g. inline columns hidden on insert forms).
 */
function singleFieldVisibility(
  columns: DataBindingSourceInfoColumn[] | undefined,
  questionColumnKey: string,
): Record<string, boolean> {
  return Object.fromEntries(
    (columns ?? []).map((column) => {
      const columnKey = getColumnKey(column);
      return [columnKey, columnKey === questionColumnKey];
    }),
  );
}

/** What the regeneration added, changed and dropped in the element graph. */
function diffElements(
  prevState: EngineStateBase,
  nextState: EngineStateBase,
): Pick<QuestionnaireRegenerationResult, 'elementsToUpsert' | 'elementIdsToRemove'> {
  return {
    elementsToUpsert: Object.values(nextState.entities).filter(
      (element): element is CanvasElement =>
        !!element && !isEqual(prevState.entities[element.id], element),
    ),
    elementIdsToRemove: Object.keys(prevState.entities).filter(
      (elementId) => !nextState.entities[elementId],
    ),
  };
}
